import { useEffect, useState } from "react";
import {
  Users, Eye, CalendarDays, CalendarRange, Calendar, Inbox, Loader2,
  Monitor, Smartphone, Tablet,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { adminApi } from "../adminApi";

const NAVY = "#0A2342";
const ACCENT = "#1E88E5";
const PIE_COLORS = ["#0A2342", "#1E88E5", "#4B5563", "#94A3B8"];

const CARDS = [
  { key: "total_visitors", label: "Total Visitors", icon: Users },
  { key: "visitors_today", label: "Visitors Today", icon: CalendarDays },
  { key: "visitors_week", label: "This Week", icon: CalendarRange },
  { key: "visitors_month", label: "This Month", icon: Calendar },
  { key: "total_page_views", label: "Total Page Views", icon: Eye },
  { key: "new_contacts", label: "New Contact Forms", icon: Inbox },
];

const DeviceIcon = ({ device }) =>
  device === "Mobile" ? <Smartphone size={14} /> : device === "Tablet" ? <Tablet size={14} /> : <Monitor size={14} />;

function Panel({ title, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-5" data-testid={testid}>
      <h3 className="font-heading font-semibold text-[#0A2342] mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get("/admin/stats").then(({ data }) => setStats(data)).finally(() => setLoading(false));
  }, []);

  if (loading)
    return <div className="flex items-center gap-2 text-[#4B5563]"><Loader2 className="animate-spin" size={18} /> Loading dashboard…</div>;
  if (!stats) return <p className="text-[#4B5563]">No data available.</p>;

  const t = stats.totals;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#0A2342]">Dashboard</h1>
        <p className="text-sm text-[#4B5563] mt-1">Website performance and visitor insights.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {CARDS.map((c) => (
          <div key={c.key} className="bg-white rounded-xl border border-[#E5E7EB] p-5" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="h-9 w-9 rounded-lg bg-[#F4F6F8] flex items-center justify-center text-[#1E88E5]">
                <c.icon size={18} />
              </span>
              {c.key === "new_contacts" && t[c.key] > 0 && (
                <span className="text-[10px] font-bold bg-[#1E88E5] text-white rounded-full px-2 py-0.5">NEW</span>
              )}
            </div>
            <p className="mt-4 text-3xl font-heading font-bold text-[#0A2342]">{t[c.key]}</p>
            <p className="text-xs text-[#4B5563] mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Daily visitors */}
      <Panel title="Daily Visitors — Last 30 Days" testid="chart-daily">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={stats.daily} margin={{ left: -20, right: 10, top: 10 }}>
            <defs>
              <linearGradient id="mvArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={ACCENT} stopOpacity={0.35} />
                <stop offset="100%" stopColor={ACCENT} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#4B5563" }} tickFormatter={(v) => v.slice(5)} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} allowDecimals={false} />
            <Tooltip />
            <Area type="monotone" dataKey="visitors" stroke={ACCENT} strokeWidth={2} fill="url(#mvArea)" />
          </AreaChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Weekly Visitors" testid="chart-weekly">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.weekly} margin={{ left: -20, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#4B5563" }} />
              <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="visitors" fill={NAVY} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Monthly Visitors" testid="chart-monthly">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.monthly} margin={{ left: -20, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#4B5563" }} />
              <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="visitors" fill={ACCENT} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Traffic Sources" testid="chart-sources">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={stats.traffic_sources} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {stats.traffic_sources.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Most Visited Pages" testid="panel-most-visited">
          <ul className="space-y-2">
            {stats.most_visited.length === 0 && <li className="text-sm text-[#4B5563]">No page views yet.</li>}
            {stats.most_visited.map((p) => (
              <li key={p.path} className="flex items-center justify-between text-sm">
                <span className="text-[#0A2342] font-medium truncate">{p.path}</span>
                <span className="text-[#4B5563] bg-[#F4F6F8] rounded px-2 py-0.5 text-xs">{p.views}</span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Recent Visitors" testid="panel-recent-visitors">
          <ul className="space-y-3">
            {stats.recent_visitors.length === 0 && <li className="text-sm text-[#4B5563]">No visitors yet.</li>}
            {stats.recent_visitors.map((v) => (
              <li key={v.session_id} className="flex items-center gap-3 text-sm border-b border-[#F4F6F8] pb-2 last:border-0">
                <span className="text-[#1E88E5]"><DeviceIcon device={v.device} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-[#0A2342] font-medium truncate">{v.city}, {v.country}</p>
                  <p className="text-xs text-[#4B5563] truncate">{v.browser} • {v.source} • {v.pages} pages</p>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
